// Title screen: the Ministry drifting behind a gilt menu. One clear column of choices; each opens
// its options in a side panel.
import { useState } from 'react';
import type { TimerSetting } from '@bcm/protocol';
import { playerName, prefs, setPlayerName, setPrefs } from '../app/identity';
import { useGame } from '../state/store';
import { Glyph } from './Card';
import { Icon } from './Icon';
import { MenuBackdrop } from './MenuBackdrop';
import { SettingsMenu } from './Hud';
import { Seg, TIMER_OPTIONS } from './Seg';
import { TOUCH } from './Tooltip';

type Pane = 'solo' | 'online' | 'how' | 'settings' | null;

/** City Resolutions and Annex rooms (P-17, P-18): the new rules, or the classic game. */
export function ResolutionsSeg({ value, onChange, readOnly }: { value: boolean; onChange: (on: boolean) => void; readOnly?: boolean }) {
  return (
    <div data-tip="City Resolutions|The first to claim a City Problem passes its Resolution: one rule changes for everyone, and some open Annex rooms. Classic plays without them.">
      <Seg
        label="City Resolutions"
        readOnly={readOnly}
        value={value ? 'on' : 'off'}
        onChange={(v) => onChange(v === 'on')}
        options={[
          { value: 'on', label: 'On' },
          { value: 'off', label: 'Classic' },
        ]}
      />
    </div>
  );
}

export function Home() {
  const [name, setName] = useState(playerName());
  const [pane, setPane] = useState<Pane>(null);
  const [bots, setBots] = useState(2);
  const [maxPlayers, setMaxPlayers] = useState(4);
  const [timer, setTimer] = useState<TimerSetting>('off');
  // City Resolutions and Annex rooms (P-17, P-18): on by default; remembered per device.
  const [resolutions, setResolutionsState] = useState<boolean>(prefs('resolutions', true));
  const setResolutions = (on: boolean) => {
    setResolutionsState(on);
    setPrefs('resolutions', on);
  };
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const g = useGame();

  const saveName = () => setPlayerName(name.trim() || 'Clerk');
  const toggle = (p: Pane) => setPane((cur) => (cur === p ? null : p));

  return (
    <main className="title-screen">
      <MenuBackdrop />
      <div className="title-col">
        <div className="title-logo">
          <span className="title-crest">
            <Icon name="quill" size={34} />
          </span>
          <h1>Bureaucromancy</h1>
          <p className="tagline">
            Send your clerks through the Ministry. Gather permits. Cast spells by the book. Become <em>The Quill Eternal</em>.
          </p>
        </div>

        <nav className="title-menu frame" aria-label="Main menu">
          <button
            className="menu-item primary"
            onClick={() => {
              saveName();
              g.setSettings({ coach: true, coachSeen: [] });
              g.startOffline(1, 'v2', resolutions);
            }}
          >
            <Icon name="compass" size={20} />
            <span>
              Guided first game
              <small>One opponent, with tips along the way</small>
            </span>
          </button>
          <button className={`menu-item ${pane === 'solo' ? 'is-on' : ''}`} onClick={() => toggle('solo')}>
            <Icon name="quill" size={20} />
            <span>
              Play vs Clerk-bots
              <small>Offline, 1–4 opponents</small>
            </span>
          </button>
          <button className={`menu-item ${pane === 'online' ? 'is-on' : ''}`} onClick={() => toggle('online')}>
            <Icon name="key" size={20} />
            <span>
              Play online
              <small>Invite friends with a link</small>
            </span>
          </button>
          <button className={`menu-item ${pane === 'how' ? 'is-on' : ''}`} onClick={() => toggle('how')}>
            <Icon name="help" size={20} />
            <span>How to play</span>
          </button>
          <button className={`menu-item ${pane === 'settings' ? 'is-on' : ''}`} onClick={() => toggle('settings')}>
            <Icon name="gear" size={20} />
            <span>Settings</span>
          </button>
          <label className="name-field">
            <span>Your name</span>
            <input value={name} maxLength={24} onChange={(e) => setName(e.target.value)} onBlur={saveName} aria-label="Display name" />
          </label>
        </nav>
      </div>

      {pane && (
        <section className="title-pane frame" aria-label="Options">
          {pane === 'solo' && (
            <>
              <h2 className="section-title">Play vs Clerk-bots</h2>
              <Seg label="Opponents" value={bots} onChange={setBots} options={[1, 2, 3, 4].map((n) => ({ value: n, label: n }))} />
              <ResolutionsSeg value={resolutions} onChange={setResolutions} />
              <button
                className="gilt-btn primary wide"
                onClick={() => {
                  saveName();
                  g.startOffline(bots, 'v2', resolutions); // always the current ruleset (v1 is for the simulator only)
                }}
              >
                Play vs Clerk-bots
              </button>
            </>
          )}
          {pane === 'online' && (
            <>
              <h2 className="section-title">Play online</h2>
              <Seg label="Seats" value={maxPlayers} onChange={setMaxPlayers} options={[2, 3, 4, 5].map((n) => ({ value: n, label: n }))} />
              <Seg label="Turn timer" value={timer} onChange={setTimer} options={[...TIMER_OPTIONS]} />
              <ResolutionsSeg value={resolutions} onChange={setResolutions} />
              <button
                className="gilt-btn primary wide"
                onClick={async () => {
                  saveName();
                  setError('');
                  try {
                    await g.createOnline({ maxPlayers, timer, preset: 'v2', resolutions });
                  } catch (e) {
                    setError(`${(e as Error).message}. Is the server running? (npm run dev:server)`);
                  }
                }}
              >
                Create a room
              </button>
              <div className="divider-or">or join one</div>
              <div className="join-row">
                <input
                  value={code}
                  placeholder="ROOM CODE"
                  maxLength={5}
                  onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z2-9]/g, ''))}
                  aria-label="Room code"
                />
                <button
                  className="gilt-btn"
                  disabled={code.length !== 5}
                  onClick={() => {
                    saveName();
                    history.pushState(null, '', `/room/${code}`);
                    g.joinOnline(code);
                  }}
                >
                  Join
                </button>
              </div>
              {error && <p className="error">{error}</p>}
            </>
          )}
          {pane === 'how' && (
            <>
              <h2 className="section-title">How to play</h2>
              <ol className="how-list">
                <li>
                  <b>Place.</b> Each turn, send one Assistant to a room and take its effect. Full rooms are closed.
                </li>
                <li>
                  <b>Collect.</b> Permits <Glyph kind="red" />
                  <Glyph kind="yellow" />
                  <Glyph kind="blue" />
                  <Glyph kind="green" /> come from the Departments; mana <Glyph kind="mana" /> from the Mana Conduit and every new round.
                </li>
                <li>
                  <b>Cast.</b> Buy spells at the Arcanium, then cast them by paying their permits. Cast spells score VP and keep working.
                </li>
                <li>
                  <b>Claim.</b> City Problems at the Grand Auditor’s Office are worth big points — first come, first served.
                </li>
                <li>
                  <b>Win.</b> After 8 rounds, the most VP becomes <em>The Quill Eternal</em>.
                </li>
              </ol>
              <p className="hint">In game, {TOUCH ? 'tap' : 'hover'} anything for details. The guided first game walks you through a turn.</p>
            </>
          )}
          {pane === 'settings' && <SettingsMenu onClose={() => setPane(null)} inGame={false} />}
        </section>
      )}
    </main>
  );
}
